import {
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { createPortal } from "react-dom";

const VIEWPORT_GUTTER = 12;
const CARD_GAP = 8;
const MAX_CARD_WIDTH = 360;

interface CardPosition {
  left: number;
  top: number;
  width: number;
}

export function HoverCard({
  label,
  trigger,
  children,
  className,
  onActivate,
}: {
  label: string;
  trigger: ReactNode;
  children: ReactNode;
  className?: string;
  onActivate?: () => void;
}) {
  const tooltipId = useId();
  const triggerRef = useRef<HTMLButtonElement>(null);
  const cardRef = useRef<HTMLDivElement>(null);
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [open, setOpen] = useState(false);
  const [position, setPosition] = useState<CardPosition>({
    left: VIEWPORT_GUTTER,
    top: VIEWPORT_GUTTER,
    width: MAX_CARD_WIDTH,
  });

  const cancelClose = useCallback(() => {
    if (closeTimer.current === null) return;
    clearTimeout(closeTimer.current);
    closeTimer.current = null;
  }, []);

  const show = useCallback(() => {
    cancelClose();
    setOpen(true);
  }, [cancelClose]);

  const hide = useCallback(() => {
    cancelClose();
    setOpen(false);
  }, [cancelClose]);

  const scheduleHide = useCallback(() => {
    cancelClose();
    closeTimer.current = setTimeout(() => {
      closeTimer.current = null;
      setOpen(false);
    }, 100);
  }, [cancelClose]);

  const updatePosition = useCallback(() => {
    const triggerElement = triggerRef.current;
    if (!triggerElement) return;
    const triggerBounds = triggerElement.getBoundingClientRect();
    const cardHeight = cardRef.current?.offsetHeight ?? 0;
    const width = Math.max(
      0,
      Math.min(MAX_CARD_WIDTH, window.innerWidth - VIEWPORT_GUTTER * 2),
    );
    const left = Math.min(
      Math.max(VIEWPORT_GUTTER, triggerBounds.left),
      Math.max(VIEWPORT_GUTTER, window.innerWidth - width - VIEWPORT_GUTTER),
    );
    const below = triggerBounds.bottom + CARD_GAP;
    const above = triggerBounds.top - CARD_GAP - cardHeight;
    const top =
      cardHeight > 0 &&
      below + cardHeight > window.innerHeight - VIEWPORT_GUTTER &&
      above >= VIEWPORT_GUTTER
        ? above
        : Math.min(
            below,
            Math.max(
              VIEWPORT_GUTTER,
              window.innerHeight - cardHeight - VIEWPORT_GUTTER,
            ),
          );
    setPosition({ left, top, width });
  }, []);

  useLayoutEffect(() => {
    if (!open) return;
    updatePosition();
    window.addEventListener("resize", updatePosition);
    window.addEventListener("scroll", updatePosition, true);
    return () => {
      window.removeEventListener("resize", updatePosition);
      window.removeEventListener("scroll", updatePosition, true);
    };
  }, [open, updatePosition]);

  useEffect(() => () => cancelClose(), [cancelClose]);

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        className={["ui-hover-card__trigger", className]
          .filter(Boolean)
          .join(" ")}
        aria-label={label}
        aria-describedby={open ? tooltipId : undefined}
        aria-expanded={open}
        onMouseEnter={show}
        onMouseLeave={scheduleHide}
        onFocus={show}
        onBlur={hide}
        onClick={() => {
          if (onActivate) {
            hide();
            onActivate();
            return;
          }
          if (open) hide();
          else show();
        }}
        onKeyDown={(event) => {
          if (event.key === "Escape") hide();
        }}
      >
        {trigger}
      </button>
      {open &&
        createPortal(
          <div
            ref={cardRef}
            id={tooltipId}
            role="tooltip"
            className="ui-hover-card__content"
            style={position}
            onMouseEnter={cancelClose}
            onMouseLeave={scheduleHide}
          >
            {children}
          </div>,
          document.body,
        )}
    </>
  );
}
